import {z} from 'zod'

import {FormValidationInput} from '../../../types'
import {ErrorCodes} from '../../common/validation/errorCodes'
import {FullLoadConfiguration} from '../../fullLoad/types'
import {validateFieldRange} from '../../common/validation/validateFieldRange'
import {QuantityConfiguration, QuantityForm} from '../types'

/**
 * Validates the quantity field for both quantity and fullLoads modes.
 * - quantity mode: checks required, valid number, closeAt, and range
 * - fullLoads mode: checks config enabled, count exists in options, min/max match
 */
export const validateQuantity = (
  data: QuantityForm,
  ctx: z.RefinementCtx,
  {configuration}: FormValidationInput<QuantityConfiguration & FullLoadConfiguration>
) => {
  const {quantity: quantityConfiguration, fullLoad: fullLoadConfiguration} = configuration

  const {quantity} = data

  // Validate fullLoads mode
  if (quantity?.type === 'fullLoads') {
    // fullLoads mode should not be possible when feature is disabled
    if (!fullLoadConfiguration.isEnabled) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['quantity'],
        message: ErrorCodes.fullLoad.notEnabled
      })
      return
    }

    // fullLoads count must match a configured option
    const matchingOption = fullLoadConfiguration.options.find(
      (opt) => opt.numberOfLoads === quantity.fullLoads
    )
    if (!matchingOption) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['quantity'],
        message: ErrorCodes.fullLoad.invalidCount
      })
      return
    }

    // min/max in form data must be consistent with the config option
    if (
      quantity.minQuantity !== matchingOption.minQuantity ||
      quantity.maxQuantity !== matchingOption.maxQuantity
    ) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['quantity'],
        message: ErrorCodes.fullLoad.staleOption
      })
    }

    return
  }

  // Check if quantity value exists
  if (!quantity || quantity.type !== 'volume' || !quantity.volume) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ['quantity'],
      message: ErrorCodes.quantity.required
    })
    return
  }

  const quantityValue = quantity.volume

  // Check if quantity is a valid number
  if (typeof quantityValue !== 'number' || isNaN(quantityValue)) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ['quantity'],
      message: ErrorCodes.quantity.notAllowed
    })
    return
  }

  // Special case: if quantity equals closeAt, it's always valid (used to close orders)
  if (
    quantityConfiguration.closeAt !== undefined &&
    quantityValue === quantityConfiguration.closeAt
  ) {
    return
  }

  // Standard range validation for all other values
  validateFieldRange(
    quantityValue,
    {
      min: quantityConfiguration.min,
      max: quantityConfiguration.max,
      step: quantityConfiguration.step,
      uom: quantityConfiguration.uom
    },
    'quantity',
    'quantity',
    ctx
  )
}
